import { useCallback } from 'react';

import type { PublicRunSnapshot } from '../../../contracts/harness.types';
import {
  CHILD_RUN_KIND,
  type ChildRunDiscovery,
} from '../../harness/child-runs/discovery';
import { useChildRunDiscoveries } from '../../harness/child-runs/use-child-run-discoveries';
import type { RunStreamEventObserver } from '../../query/useRunStreamLifecycle';

export interface ProjectRunStreamObserver {
  /**
   * Recording sub-agents discovered on this run's per-run stream +
   * snapshot, awaiting the dedicated recording executor attach.
   * Per-run-local (no global store). Only `ProjectsBackgroundRunner`
   * consumes these for attach — the chat transport does not.
   */
  recordingDiscoveries: ChildRunDiscovery[];
  /**
   * Single observer to wire into the parent run's `onStreamEvent`. Fans the
   * per-run LLM stream out to every project-side consumer that rides it:
   * recording child discovery (`tool_execution_start` metadata), then the
   * owner's `onForeignStreamEvent` (for the chat: Evidence sub-agent chunk
   * ingestion and Slide Builder patches).
   */
  onStreamEvent: RunStreamEventObserver;
  /**
   * Reconcile recording discoveries from a run snapshot's `child_runs`. No-ops while the snapshot is undefined.
   * Drive it from a `[run]` effect.
   */
  reconcileFromSnapshot: (run: PublicRunSnapshot | null | undefined) => void;
}

export interface ProjectRunStreamObserverOptions {
  /**
   * The run's stream consumers outside this domain, handed every event after the
   * discoveries: Evidence's `evidence.chunk` ingest into `evidenceSessionsAtom`
   * and Slide Builder's patches to the open deck. Exactly one owner per run may
   * pass one: the chat transport and the background runner each open their OWN
   * unordered SSE connection to the same parent run, and the Evidence reducer
   * appends and can't fold out-of-order indices — so two ingesters would
   * duplicate or corrupt the timeline, and a `slide_update` applied from two
   * connections would double-insert an added slide. The chat transport is the
   * canonical owner because evidence only renders there; the background runner
   * and the overlay pass none. Pass a module-level function: the observer's
   * identity follows it.
   */
  onForeignStreamEvent?: RunStreamEventObserver;
}

/**
 * Composes the per-run consumers that read a project parent run's LLM
 * stream into one `onStreamEvent` observer, shared by `useProjectTransport`
 * (chat surface) and the background runner so neither duplicates the wiring.
 *
 * Evidence chunks ride the parent run's stream because the Evidence
 * sub-agent is a headless tool loop inside the parent run — there is no
 * separate child run to subscribe to (unlike recording, which opens a child
 * stream from its discoveries). Recording discovery is per-owner local state
 * (idempotent across both owners), but evidence folds into a shared atom, so
 * it is gated to a single owner: the one that passes `onForeignStreamEvent`.
 */
export const useProjectRunStreamObserver = (
  agentRunId: string | undefined,
  { onForeignStreamEvent }: ProjectRunStreamObserverOptions = {}
): ProjectRunStreamObserver => {
  const {
    discoveries: recordingDiscoveries,
    onParentStreamEvent: onRecordingStreamEvent,
    reconcileFromSnapshot: reconcileRecording,
  } = useChildRunDiscoveries(CHILD_RUN_KIND.recording, agentRunId);

  const onStreamEvent = useCallback<RunStreamEventObserver>(
    (id, event) => {
      onRecordingStreamEvent(id, event);
      onForeignStreamEvent?.(id, event);
    },
    [onRecordingStreamEvent, onForeignStreamEvent]
  );

  const reconcileFromSnapshot = useCallback(
    (run: PublicRunSnapshot | null | undefined) => {
      reconcileRecording(run);
    },
    [reconcileRecording]
  );

  return {
    recordingDiscoveries,
    onStreamEvent,
    reconcileFromSnapshot,
  };
};
